import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack, Group, Card, Text, Button, Badge, Center, Loader, Alert, Box,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ChevronLeft, Square } from 'lucide-react';

import { gamesApi } from '../api';
import { modeMeta } from '../components/modeMeta';
import { WalkieResults } from '../components/results/WalkieResults';
import {
  HostinaResults, PocitadloResults, StopkyResults, DominationsResults, MrtvolisteResults,
} from '../components/results/SimpleResults';
import { MvpCard } from '../components/results/MvpCard';
import { numStyle } from '../theme';

function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

export default function GameDetailPage() {
  const { uuid = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const game = useQuery({
    queryKey: ['game', uuid],
    queryFn: () => gamesApi.getById(uuid),
    enabled: !!uuid,
  });

  const end = useMutation({
    mutationFn: () => gamesApi.endGame(uuid),
    onSuccess: () => {
      notifications.show({ message: 'Hra ukončena' });
      qc.invalidateQueries({ queryKey: ['game', uuid] });
      qc.invalidateQueries({ queryKey: ['games'] });
    },
    onError: (e: any) =>
      notifications.show({
        title: 'Hru se nepodařilo ukončit',
        message: e?.response?.data?.error || e.message,
        color: 'red',
      }),
  });

  if (game.isLoading) return <Center h={240}><Loader /></Center>;
  if (game.error || !game.data) {
    return (
      <Stack gap="sm">
        <Button variant="default" leftSection={<ChevronLeft size={14} />} onClick={() => navigate('/hry')} w="fit-content">
          Hry
        </Button>
        <Alert color="red" title="Hru se nepodařilo načíst">
          {(game.error as any)?.response?.data?.error || (game.error as any)?.message || 'Neexistuje.'}
        </Alert>
      </Stack>
    );
  }

  const g = game.data;
  const live = !g.ended_at;
  const meta = modeMeta(g.game_type_id);
  const Icon = meta.icon;

  return (
    <Stack gap="sm">
      <Card padding={0}>
        <Group p="sm" gap="sm" wrap="wrap">
          <Button size="xs" variant="subtle" color="gray" leftSection={<ChevronLeft size={14} />}
            onClick={() => navigate('/hry')}>
            Hry
          </Button>
          <Icon size={16} color={`var(--mantine-color-${meta.color}-6)`} />
          <Text fw={700} fz="md" style={{ flex: 1, minWidth: 0 }} truncate>{g.name}</Text>
          {live ? (
            <Badge color="flag" leftSection={<span className="live-dot" />}>
              běží od {when(g.created_at)}
            </Badge>
          ) : (
            <Badge color="gray">ukončena {when(g.ended_at)}</Badge>
          )}
          {live && (
            <Button
              size="xs" variant="default" color="red" leftSection={<Square size={13} />}
              loading={end.isPending}
              onClick={() => {
                if (window.confirm(`Ukončit hru ${g.name}? Zařízení přestanou zapisovat.`)) end.mutate();
              }}
            >
              Ukončit hru
            </Button>
          )}
        </Group>
        <Box px="sm" pb="sm">
          <Text c="dimmed" fz="xs" style={numStyle}>{meta.label} · {g.uuid}</Text>
        </Box>
      </Card>

      <ResultsFor mode={g.game_type_id} uuid={uuid} live={live} />

      {!live && <MvpCard gameUuid={uuid} />}
    </Stack>
  );
}

function ResultsFor({ mode, uuid, live }: { mode?: string | null; uuid: string; live: boolean }) {
  switch (mode) {
    case 'walkie':      return <WalkieResults gameUuid={uuid} live={live} />;
    case 'dominations': return <DominationsResults gameUuid={uuid} />;
    case 'stopky':      return <StopkyResults gameUuid={uuid} />;
    case 'hostina':     return <HostinaResults gameUuid={uuid} />;
    case 'pocitadlo':   return <PocitadloResults gameUuid={uuid} />;
    case 'mrtvoliste':  return <MrtvolisteResults gameUuid={uuid} />;
    default:
      // Mrtvoliště jede i jako overlay nad jinou hrou, takže skóre dává smysl vždycky.
      return <MrtvolisteResults gameUuid={uuid} />;
  }
}
